import type { ReactNode } from 'react'
import { StoryHero } from '../story-hero/StoryHero.tsx'
import { NewsCard } from '../news-card/NewsCard.tsx'
import { PostRow } from '../post-row/PostRow.tsx'
import styles from './NewsFront.module.css'

/** A story as every news component takes it. */
export interface NewsStory {
  id: string
  to: string
  title: string
  excerpt?: string
  coverUrl?: string | null
  kicker?: string
  featured?: boolean
  author?: string
  date?: string | null
  readTimeMinutes?: number
}

export interface NewsFrontProps {
  /** Stories in order: the first leads, the next two sit beside it, the rest run under Latest. */
  stories: NewsStory[]
  /** The right-hand column: schedule, calls to action. Stacks under the river on a phone. */
  rail?: ReactNode
  /** Shown under the Latest river, e.g. a load-more sentinel or a gallery. */
  children?: ReactNode
  latestLabel?: string
  /**
   * `front` leads with a hero and two stories; `river` lists every story as a
   * row, for search results.
   */
  layout?: 'front' | 'river'
  className?: string
}

/**
 * A news front page, ESPN-style: a lead story with two beside it, then a
 * river of the rest next to a rail.
 */
export function NewsFront({ stories, rail, children, latestLabel = 'Latest', layout = 'front', className }: NewsFrontProps) {
  const isRiver = layout === 'river'
  const [lead, ...others] = isRiver ? [] : stories
  const secondary = others.slice(0, 2)
  const river = isRiver ? stories : others.slice(2)

  return (
    <div className={[styles.front, className].filter(Boolean).join(' ')}>
      {lead && (
        <section className={[styles.top, secondary.length === 0 ? styles.solo : ''].filter(Boolean).join(' ')} aria-label="Top stories">
          <StoryHero
            to={lead.to}
            title={lead.title}
            excerpt={lead.excerpt}
            coverUrl={lead.coverUrl}
            kicker={lead.kicker}
            featured={lead.featured}
            author={lead.author}
            date={lead.date}
            readTimeMinutes={lead.readTimeMinutes}
          />
          {secondary.length > 0 && (
            <div className={styles.secondary}>
              {secondary.map((s) => (
                <NewsCard
                  key={s.id}
                  to={s.to}
                  title={s.title}
                  coverUrl={s.coverUrl}
                  kicker={s.kicker}
                  author={s.author}
                  date={s.date}
                  readTimeMinutes={s.readTimeMinutes}
                />
              ))}
            </div>
          )}
        </section>
      )}

      {(river.length > 0 || children || rail) && (
        <div className={[styles.lower, rail ? '' : styles.noRail].filter(Boolean).join(' ')}>
          <div className={styles.main}>
            {river.length > 0 && (
              <section aria-labelledby="news-latest">
                <h2 id="news-latest" className={styles.sectionTitle}>{latestLabel}</h2>
                {river.map((s) => (
                  <PostRow
                    key={s.id}
                    to={s.to}
                    title={s.title}
                    excerpt={s.excerpt}
                    coverUrl={s.coverUrl}
                    kicker={s.kicker}
                    author={s.author}
                    date={s.date}
                    readTimeMinutes={s.readTimeMinutes}
                  />
                ))}
              </section>
            )}
            {children}
          </div>
          {rail && <aside className={styles.rail}>{rail}</aside>}
        </div>
      )}
    </div>
  )
}
